/**
 * Billing settings page (Phase 5).
 *
 * SCOPE — charge-facing only. This page answers "what am I paying, and what
 * will I be charged next?": plan, seats, included budget consumed,
 * overage-to-date, invoices, payment method, and the past_due state.
 *
 * It deliberately does NOT show per-user / per-team / per-role spend or cap
 * editors. That is the budget-governance dashboard on `feat/budget-governance`
 * ("who spent it, and what are their limits"). Keeping the two apart is what
 * stops the same numbers being rendered twice from two sources. See the
 * "Interface with feat/budget-governance" section of the billing plan.
 *
 * Rendered into SettingsView through the `billingPage` render-prop slot —
 * @papan/core-ui may not import from features/* (shared/* → features/* is a
 * one-way boundary), and a slot also keeps our footprint in that 3,700-line
 * file to a handful of lines while a colleague edits it too.
 */
import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle, CalendarClock, CheckCircle2, ChevronRight, CreditCard, ExternalLink, HardDrive, Info,
  Loader2, RefreshCw, Receipt, Settings, Sparkles, Users, XCircle,
} from 'lucide-react'
import { useConfirm, useToast, Tooltip } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { type PlanView, type SubscriptionTermView } from '../client/index.js'
import {
  billingKeys,
  mirrorWait,
  takeCreditWait,
  useCancelSubscription,
  useChangePlan,
  useInvoices,
  useOpenPortal,
  usePaymentMethod,
  usePlans,
  useSubscribe,
  useManualStatement,
  useSubscription,
  useSubscriptionTerms,
  useStorageComposition,
  type CreditWait,
  type MirrorWait,
} from '../client/queries.js'
import { CreditsSection } from './CreditsSection.js'
import { ActivePromoSection } from './ActivePromoSection.js'
import { formatBillingMicro, formatCurrencyMinor, formatCurrencyDate, formatCurrencyDateLocal, formatStorage, meterPct } from './money.js'

/**
 * Currency fallback for a payload from an API instance that predates migration
 * 0147 (rolling deploy). Every row such an instance has is MYR by definition,
 * so this renders exactly what shipped before rather than an unlabeled number.
 */
const FALLBACK_CURRENCY = 'myr'
import { BillingSkeleton } from './BillingSkeleton.js'
import { CARD, NUMERIC, SECTION_LABEL } from './styles.js'

interface Props {
  /** Owner/admin gate. Members see the read-only summary; only billing admins
   *  get the plan/cancel/portal controls, matching the server's own gate so
   *  the UI never offers an action that will 403. */
  canManageBilling: boolean
  /**
   * Opens the detailed spend breakdown — the governance dashboard ("who spent
   * it") for a caller with budget-read, otherwise the self-scoped Usage page.
   * This page stays charge-facing on purpose; the link is the bridge between
   * the two, not a reason to render the same numbers twice. Omitted only when
   * the caller can offer neither, which hides the link.
   *
   * Both shells derive it through `billingHandoff()`, which always supplies it
   * — the destination differs, the availability does not. It stays optional so
   * the component still renders in a host that offers no navigation at all
   * (tests, storybook-style mounts).
   */
  onOpenBudgets?: () => void
  /**
   * Whether to print the note about MEMBERS' allowances running on the
   * calendar month.
   *
   * A separate prop from `onOpenBudgets`, and the separation is the fix for a
   * real defect. Both used to hang off that one prop, so silencing the
   * sentence on a personal drive would have meant withholding the hand-off
   * button — which is *correct* there, since a personal drive has its own
   * Spending page. One is a fact about the TENANT'S SHAPE (are there members?),
   * the other about the READER'S PERMISSIONS (where should this link go?).
   * They are not the same question and must not share a gate.
   *
   * Defaults to false: a host that has not thought about it says nothing,
   * rather than asserting members exist.
   */
  showMembersClockNote?: boolean
  /**
   * Opens the members/seats page so an admin can add billable seats — wired to
   * the org's members settings. Omitted for a personal tenant (no members page)
   * or when the caller can't offer it, which hides the "Add seats" button.
   */
  onManageSeats?: () => void
  /**
   * Opens Settings → Storage — the storage tile's hand-off on the manual-mode
   * key-info card. Both shells always supply it (every tenant has a storage
   * page); optional so a bare mount without navigation still renders.
   */
  onOpenStorage?: () => void
  /**
   * Opens the members page — the included-seats tile's hand-off. Undefined on a
   * personal tenant (no members page), which leaves the tile a static figure.
   */
  onOpenMembers?: () => void
  /**
   * Whether the caller may see the subscription-term timeline —
   * `manage_billing:read`, the exact gate `GET /subscription-terms` enforces
   * server-side (and the same one `useSubscription` already sits behind).
   *
   * Optional, and `undefined` by DEFAULT renders the section: every existing
   * host (`App.tsx`, `MobileShell.tsx`) only ever mounts `BillingPage` once
   * its own `manage_billing:read` check (`canViewBilling`) has RESOLVED to
   * granted, so by construction every caller reaching this component already
   * holds the read grant — the server route is the real enforcement, this is
   * a courtesy. A host that wants its own resolved answer honoured — or a
   * test exercising the loading-vs-denial discipline in isolation — passes
   * `true`/`false` explicitly. `false` is read as `undefined` (shows) while
   * `subscriptionTermsLoading` is true; see that prop.
   */
  canViewSubscriptionTerms?: boolean
  /**
   * Set while a HOST-level `manage_billing:read` check for the timeline is
   * still in flight. `can()`-style checks report "still fetching" and
   * "denied" identically as `false` (the trap `SpendingPage` guards against —
   * see CLAUDE.md's "grant-loading-vs-denial" section), so treating an
   * unresolved answer as denial would flash a refusal at an admin whose grant
   * simply hasn't loaded yet. While this is `true` the section renders
   * NEITHER the timeline NOR a refusal — it waits. Defaults to `false`
   * (resolved), so an omitted prop is never stuck loading.
   */
  subscriptionTermsLoading?: boolean
}

/** Statuses where Stripe is still trying to collect — the dunning banner. */
const DUNNING_STATUSES: ReadonlySet<string> = new Set(['past_due', 'unpaid', 'incomplete'])

/**
 * Floor on how long the refreshing state stays visible, so a fast response
 * reads as "reloaded" rather than as a flicker.
 *
 * Deliberately attached to a visual affordance (dimmed content + the button's
 * spinner) and NOT to a skeleton. A skeleton held for this long would withhold
 * figures we already have in memory in order to show placeholders for data that
 * has already arrived — the user pressed Refresh to see the numbers, so hiding
 * them is the one thing the interaction must not do. Dimming costs nothing: the
 * content stays readable for the whole window.
 */
const MIN_REFRESH_FEEDBACK_MS = 500

/** Meter colour by consumption — green/blue while healthy, amber approaching
 *  the allowance, red once past it. Colour is never the ONLY signal; the
 *  figures above the bar always state the same thing in text. */
function meterTone(pct: number): string {
  if (pct >= 100) return 'bg-red-500'
  if (pct >= 75) return 'bg-amber-500'
  // Healthy carries the design's lavender accent (--bill-primary), plus a soft
  // primary-container (#9333ea) glow so the fill reads as a lit gauge rather
  // than a flat bar. The glow is scoped to this arm only — an amber/red glow in
  // a purple hue would misstate the state.
  return 'bg-[color:var(--bill-primary)] shadow-[0_0_12px_rgba(147,51,234,0.45)]'
}

/** A hover ⓘ carrying a tile's helper text — the key-info card keeps its faces
 *  clean and moves every explanatory sentence into a tooltip. `always` so it
 *  fires on hover regardless of overflow (the info is not visible in the icon). */
function InfoHint({ text }: { text: string }) {
  return (
    <Tooltip content={text} always placement="top" className="inline-flex items-center">
      <Info size={11} className="themed-text-muted cursor-help" />
    </Tooltip>
  )
}

/**
 * Subscription status as a chip beside the plan name.
 *
 * Promoted out of the meta list because it qualifies the headline fact — a
 * plan that is past_due is not the same thing as an active one, and burying
 * that in a row of dates read as if it were incidental. Tone is carried by
 * colour AND the label text, never colour alone.
 */
function StatusPill({ status, label }: { status: string; label: string }) {
  const tone =
    status === 'active' || status === 'trialing'
      ? 'bg-emerald-500/12 text-emerald-400 ring-emerald-500/25'
      : DUNNING_STATUSES.has(status)
        ? 'bg-red-500/12 text-red-400 ring-red-500/25'
        : 'themed-bg-tertiary themed-text-secondary ring-transparent'
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ring-1 ${tone}`}>
      {label}
    </span>
  )
}

export function BillingPage({
  canManageBilling, onOpenBudgets, onOpenStorage, onOpenMembers, onManageSeats, showMembersClockNote = false,
  canViewSubscriptionTerms, subscriptionTermsLoading = false,
}: Props) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const confirm = useConfirm()

  const queryClient = useQueryClient()

  /**
   * Set after a write to describe the change we expect the webhook mirror to
   * show. While it's set, `useSubscription` polls; it clears as soon as the
   * change lands or the deadline passes. Null the rest of the time, so an idle
   * billing page issues no background traffic.
   */
  const [awaiting, setAwaiting] = useState<MirrorWait | null>(null)
  /**
   * The same idea for a credit top-up (C5), kept separate because it waits on a
   * different endpoint and a different kind of evidence: the subscription
   * mirror waits for a row to CHANGE SHAPE, this waits for a NUMBER TO RISE
   * past what it was before we handed off to Stripe.
   */
  const [awaitingCredits, setAwaitingCredits] = useState<CreditWait | null>(null)
  /** When the current manual refresh began; null when not refreshing. */
  const [refreshStartedAt, setRefreshStartedAt] = useState<number | null>(null)

  const subQuery = useSubscription(awaiting)
  const plansQuery = usePlans()
  /**
   * Does a PROVIDER decide this tenant's plan?
   *
   * Deliberately NOT folded into `canAct`. That asks "may you?" — a permission
   * question about the caller. This asks "is there anything here to show?" — a
   * deployment question about the world. Conflating them is how the invoices
   * table came to render, permanently empty, for a tenant that can never have
   * an invoice: the check said `canAct` when it meant this.
   *
   * Under `'manual'` an operator assigns plans and invoices out of band, so the
   * picker, the payment method, the invoice history and the dunning banner all
   * describe machinery that is not running. They are HIDDEN rather than
   * disabled: a greyed-out "Select plan" still advertises a purchase, and the
   * customer would be right to ask why it does not work.
   *
   * Note this changes what an OWNER sees. A plain member already saw none of
   * these — they hang off `canAct`, and `canManageBilling` is false without
   * `manage_billing:write`.
   */
  const providerBilled = (plansQuery.data?.planAuthority ?? 'provider') === 'provider'
  // Fetched whenever the caller may see them, NOT only under provider
  // authority: a manually-billed tenant that once paid Stripe still owns that
  // history, and skipping the read would make it unreachable rather than merely
  // hidden. The empty case costs one call and renders nothing (below).
  const invoicesQuery = useInvoices(canManageBilling)
  // Fetched for everyone with the page: the paid-through date is not admin-only
  // (it explains a write freeze the member can already see), and the server
  // decides whether the money rides along.
  const statementQuery = useManualStatement(true)
  // Card details are owner/admin-only and read live from Stripe, so only fetch
  // when billing is actually enabled on this install (the route 404s otherwise).
  const paymentMethodQuery = usePaymentMethod(
    canManageBilling && (plansQuery.data?.billingEnabled ?? false),
  )
  // The manual-mode key-info card surfaces storage-used. Gated to manual mode —
  // the only place the card renders — so a provider-billed page skips the drive
  // walk. (The prepaid "extra credits" balance is no longer read here: it moved
  // to its own dedicated Usage-credits card, which issues its own read.)
  const storageQuery = useStorageComposition(!providerBilled)
  // The subscription-term timeline (Task 19). Withheld while a host-level
  // capability check is still in flight (see `subscriptionTermsLoading`'s doc)
  // — there is no point fetching a read the render is about to hide anyway.
  const showSubscriptionTerms = canViewSubscriptionTerms !== false
  const termsQuery = useSubscriptionTerms(showSubscriptionTerms && !subscriptionTermsLoading)

  const changePlanMut = useChangePlan()
  const cancelMut = useCancelSubscription()
  const subscribeMut = useSubscribe()
  const portalMut = useOpenPortal()

  const data = subQuery.data ?? null
  const plans = plansQuery.data?.plans ?? []
  /** Write routes 404 when BILLING_ENABLED is off; reads stay live. */
  const writable = plansQuery.data?.billingEnabled ?? false
  const invoices = invoicesQuery.data?.invoices ?? []
  const loading = subQuery.isPending || plansQuery.isPending
  const loadError = subQuery.error ?? plansQuery.error
  /** Drives the "finalizing" banner. */
  const syncing = awaiting !== null
  /** Any read in flight, including background ones (the mirror poll). */
  const refreshing = subQuery.isFetching || plansQuery.isFetching || invoicesQuery.isFetching
  /** A MANUAL refresh specifically — drives the dim and the button state. */
  const isRefreshing = refreshStartedAt !== null

  /**
   * Which control is mid-flight. Derived from the mutations rather than tracked
   * by hand; `variables` is the planId the mutation was called with, so the
   * per-row spinner still keys off the same `busy === p.id` comparison.
   */
  const busy: string | null =
    changePlanMut.isPending ? changePlanMut.variables ?? null
    : subscribeMut.isPending ? subscribeMut.variables ?? null
    : cancelMut.isPending ? 'cancel'
    : portalMut.isPending ? 'portal'
    : null

  // The mirror caught up — stop polling.
  useEffect(() => {
    if (!awaiting || !subQuery.data) return
    if (awaiting.done(subQuery.data)) setAwaiting(null)
  }, [awaiting, subQuery.data])

  // ...or it never did. Give up on schedule so a stuck webhook degrades to the
  // normal page with a Refresh button instead of polling forever.
  useEffect(() => {
    if (!awaiting) return
    const timer = setTimeout(() => setAwaiting(null), Math.max(0, awaiting.deadline - Date.now()))
    return () => clearTimeout(timer)
  }, [awaiting])

  /**
   * Manual refresh.
   *
   * Tracked via `refreshStartedAt` (set only by the button) rather than by
   * `refreshing` (isFetching), which is also true for the post-write mirror
   * poll — driving the visual off isFetching would pulse the whole page every
   * 1.5s while waiting on a webhook.
   */
  const refresh = useCallback(() => {
    setRefreshStartedAt(Date.now())
    void queryClient.invalidateQueries({ queryKey: billingKeys.all })
  }, [queryClient])

  /**
   * Hold the refreshing state for MIN_REFRESH_FEEDBACK_MS once shown.
   *
   * Locally the reads answer in tens of milliseconds, so without a floor the
   * indicator appeared and vanished within a frame or two — read as a flicker,
   * not as feedback. The floor is on the INDICATOR, never on the request:
   * nothing sleeps before the fetch, so a refresh slower than the floor pays no
   * penalty, and no information is withheld while it elapses.
   */
  useEffect(() => {
    if (refreshStartedAt === null || refreshing) return
    const remaining = MIN_REFRESH_FEEDBACK_MS - (Date.now() - refreshStartedAt)
    const timer = setTimeout(() => setRefreshStartedAt(null), Math.max(0, remaining))
    return () => clearTimeout(timer)
  }, [refreshStartedAt, refreshing])

  /**
   * Post-checkout landing. Stripe returns the customer to
   * `<prefix>/billing/subscription?checkout=success|cancelled`, and without this
   * the page looked identical whether they had just paid or just backed out.
   *
   * The param is stripped immediately so a refresh or a back-navigation doesn't
   * replay the toast against stale state.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const outcome = params.get('checkout')
    // Credits return on `?credits=`, subscriptions on `?checkout=` — separate
    // params because they are separate purchases with separate waits, and a
    // shared one would make a credit top-up poll the subscription mirror.
    const credits = params.get('credits')
    if (!outcome && !credits) return

    params.delete('checkout')
    params.delete('credits')
    params.delete('session_id')
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))

    if (outcome === 'cancelled') {
      toast.info(t('checkout.cancelled'))
    } else if (outcome === 'success') {
      toast.success(t('checkout.success'))
      setAwaiting(mirrorWait((sub) => sub.subscription !== null))
    }

    if (credits === 'cancelled') {
      toast.info(t('credits.checkout.cancelled'))
      // Drain the stash too. Nothing was charged, so leaving it behind would
      // let an abandoned purchase supply the "before" balance for a later one.
      takeCreditWait()
    } else if (credits === 'success') {
      toast.success(t('credits.checkout.success'))
      // Null when the tab that started checkout isn't this one (or storage is
      // unavailable). The section then just shows whatever the next read
      // returns — a late balance, never a wrong one.
      setAwaitingCredits(takeCreditWait())
    }
    // Mount-only: the param is consumed and erased on the first pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Bound the credit wait the same way the subscription one is bounded: a
  // webhook that never arrives must degrade to the ordinary page, not poll
  // forever.
  useEffect(() => {
    if (!awaitingCredits) return
    const timer = setTimeout(
      () => setAwaitingCredits(null),
      Math.max(0, awaitingCredits.deadline - Date.now()),
    )
    return () => clearTimeout(timer)
  }, [awaitingCredits])

  const creditsSettled = useCallback(() => setAwaitingCredits(null), [])

  /**
   * Redirect to a Stripe-hosted flow. `window.location.assign` rather than
   * opening a tab: Checkout and the portal both return the customer to us via
   * their own return_url, so a same-tab hand-off keeps one continuous journey.
   */
  const redirectTo = async (run: () => Promise<{ url: string }>) => {
    try {
      const { url } = await run()
      window.location.assign(url)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.generic'))
    }
  }

  const cancel = useCallback(async () => {
    const ok = await confirm({
      title: t('cancel.title'),
      message: t('cancel.message', { date: fmtDate(data?.subscription?.currentPeriodEnd) }),
      confirmText: t('cancel.confirmAction'),
      type: 'danger',
    })
    if (!ok) return
    try {
      await cancelMut.mutateAsync()
      toast.success(t('cancel.done'))
      // Cancellation is deferred to period end, so the row stays 'active' and
      // only cancelAtPeriodEnd flips — that's the flag to wait on.
      setAwaiting(mirrorWait((sub) => sub.subscription?.cancelAtPeriodEnd === true))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.generic'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirm, t, toast, data?.subscription?.currentPeriodEnd])

  const changePlan = async (plan: PlanView) => {
    const current = data?.plan
    const subscription = data?.subscription

    // "Move to Free" is not a plan change — Stripe has no free price to switch
    // to, so /change-plan rejects it ("target plan is not available"). What the
    // customer means is "stop paying", which is exactly what /cancel does:
    // keep the paid plan until the period ends, then fall back to Free.
    // `null` (unpriced) is NOT "free": cancelling a customer because their plan
    // has a catalogue gap would end a subscription they are still paying for.
    if (plan.basePriceMinor === 0) {
      if (!subscription) return // already on Free; the button is disabled anyway
      await cancel()
      return
    }

    // An unpriced side compares as the cheapest thing possible, which routes
    // the change through the IMMEDIATE branch — the customer gets what they
    // selected now rather than at a boundary. Same rule as the server's.
    const isUpgrade = !current || (plan.basePriceMinor ?? 0) > (current.basePriceMinor ?? 0)
    const ok = await confirm({
      title: t('planPicker.confirmTitle', { plan: plan.name }),
      // Downgrades are deferred to period end and upgrades are immediate +
      // prorated. Saying which BEFORE they commit avoids the "why was I
      // charged today" support ticket.
      message: isUpgrade
        ? t('planPicker.confirmUpgrade', { plan: plan.name })
        : t('planPicker.confirmDowngrade', { plan: plan.name }),
      confirmText: t('planPicker.confirmAction'),
    })
    if (!ok) return

    try {
      if (subscription) {
        const res = await changePlanMut.mutateAsync(plan.id)
        toast.success(
          res.effective === 'immediate'
            ? t('planPicker.changedNow', { plan: plan.name })
            : t('planPicker.changedAtPeriodEnd', { plan: plan.name }),
        )
        // An upgrade swaps the plan now; a downgrade only queues one, so the
        // observable change is pendingPlanId rather than the active plan.
        //
        // Both arms ALSO require cancelAtPeriodEnd to be clear. Choosing a plan
        // supersedes a pending cancellation and the server drops it, but that
        // clearing only reaches us via the webhook. pendingPlanId alone is a
        // useless "did it land" signal here — the server writes it to our own
        // DB synchronously, so the predicate would pass on the first refetch
        // and stop polling while cancelAtPeriodEnd was still stale-true. That
        // left BOTH the cancellation and the new plan showing as scheduled
        // until a manual page refresh.
        setAwaiting(
          mirrorWait((sub) => {
            if (sub.subscription?.cancelAtPeriodEnd) return false
            return res.effective === 'immediate'
              ? sub.subscription?.planId === plan.id
              : sub.subscription?.pendingPlanId === plan.id
          }),
        )
      } else {
        await redirectTo(() => subscribeMut.mutateAsync(plan.id))
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.generic'))
    }
  }

  // FIRST load only — there is genuinely nothing to show yet. A refresh keeps
  // its content and dims instead; see MIN_REFRESH_FEEDBACK_MS.
  if (loading) {
    return <BillingSkeleton canManageBilling={canManageBilling} />
  }

  if (loadError || !data) {
    return (
      <div className="p-4 space-y-3">
        <p className="text-sm text-red-400">
          {loadError instanceof Error ? loadError.message : t('errors.generic')}
        </p>
        <button onClick={refresh} className="text-xs themed-btn-secondary px-3 py-1.5 rounded-md">
          {t('retry')}
        </button>
      </div>
    )
  }

  const { plan, subscription, seats, usage, tenant: tenantLimits } = data
  /**
   * Storage the card will print, or `null` to omit the row entirely.
   *
   * `formatStorage` already returns `null` for a null/zero quota so the caller
   * "drops the bullet entirely rather than printing '0 GB'" — the same reason
   * applies here, plus one more: `tenantLimits` is absent on a payload from an
   * older API instance mid-rolling-deploy, and a card claiming 0 GB of storage
   * to a paying customer is worse than a card that briefly omits the row.
   */
  const storageLabel = typeof tenantLimits?.storageQuotaBytes === 'number'
    ? formatStorage(tenantLimits.storageQuotaBytes)
    : null
  /**
   * The DEPLOYMENT's billing currency — one code for every amount on this page.
   *
   * It rides on the plan view because that is where the server resolves it, but
   * it is not a property of the plan: since Gate 1 a plan is priced per
   * currency and the server only ever quotes the one this tenant is billed in.
   * The fallback is for a payload from an API instance mid-rolling-deploy.
   */
  const currency = plan.currency ?? FALLBACK_CURRENCY
  /** Wall-clock INSTANT (Stripe period/invoice boundaries, `paid_through`) in
   *  the page's currency locale (M/D/YYYY for USD, D/M/YYYY for MYR), rendered
   *  in LOCAL time — these are real timestamps, not midnight-UTC civil days, so
   *  UTC-pinning them would shift the day for viewers near a day boundary. The
   *  term timeline's `effectiveFrom` IS a civil day and uses `formatCurrencyDate`
   *  (UTC) directly. */
  const fmtDate = (iso: string | null | undefined) => formatCurrencyDateLocal(iso, currency)
  /**
   * This period's included allowance, billing micro. `null` = the plan carries
   * no price in this deployment's currency, which is a catalogue gap rather
   * than a zero allowance — the meter reads as "no allowance" rather than
   * drawing a full bar over a number nobody measured.
   */
  const allowance = usage.includedBudgetBillingMicro
  // Stripe's collection states. Deliberately NOT gated on `providerBilled`,
  // though an earlier pass did: this already requires a LIVE SUBSCRIPTION, and
  // if one exists and is past_due then a real card is really failing — whatever
  // the deployment's authority setting says. An operator migrating a
  // provider-billed deployment to manual invoicing would otherwise silence the
  // "your payment failed" banner for every customer mid-migration, which is the
  // one moment it matters most. Authority decides what may be BOUGHT; a
  // subscription that exists is a fact about this tenant.
  const dunning = subscription && DUNNING_STATUSES.has(subscription.status)

  /**
   * What happens at the period boundary. A queued plan change and a queued
   * cancellation are mutually exclusive — each server route clears the other —
   * so they are derived together here and used everywhere, rather than each
   * render site testing one flag on its own.
   *
   * If both are momentarily set (the un-cancel is mirrored by webhook, so there
   * is a window where our row still says cancelling), the plan change is the
   * newer intent and wins. Reading the flags independently is what let the Free
   * row and the Starter row both claim "Scheduled" at the same time.
   */
  const scheduledPlanId = subscription?.pendingPlanId ?? null
  const scheduledCancel = subscription?.cancelAtPeriodEnd === true && !scheduledPlanId
  /** Resolved from the catalog so the note can name the plan, not its id. */
  const pendingPlan = scheduledPlanId
    ? plans.find((p) => p.id === scheduledPlanId) ?? null
    : null
  // Every mutating control keys off this, not `canManageBilling` alone: an
  // owner on a BILLING_ENABLED=0 install is authorized but the routes 404.
  const canAct = canManageBilling && writable
  const paymentMethod = paymentMethodQuery.data?.paymentMethod ?? null
  const statement = statementQuery.data ?? null
  // Buckets the allowance meter cannot represent, each rendered only when it
  // carries spend. Every field is optional on the wire (rolling deploy), so read
  // them defensively — an older API instance shows nothing here rather than
  // throwing mid-render.
  const otherFunding = ([
    [usage.promoBillingMicro ?? 0, 'usage.fundedPromo'],
    [usage.prepaidBillingMicro ?? 0, 'usage.fundedPrepaid'],
    [usage.graceBillingMicro ?? 0, 'usage.fundedGrace'],
  ] as const)
    .filter(([amount]) => amount > 0)
    // No brand argument: `fundedGrace` says `{{productName}}`, which
    // `setBrandInterpolationVars` supplies to EVERY string as an i18next
    // default variable (see `@papan/i18n`). A literal deployment name here
    // would be a whitelabel regression on a money-facing screen — passing it
    // per call site is the same fix done once per caller instead of once.
    .map(([amount, key]) => t(key, { amount: formatBillingMicro(amount, currency) }))
  const includedUsed = usage.freeBillingMicro
  /**
   * Key-info card figures (manual mode). Prepaid balance is the spendable
   * top-up total shown as "extra credits". Storage USED is summed the same way
   * the storage page and the quota enforcer sum it (live + bin + versions +
   * quarantine); the QUOTA comes from the tenant's enforced column so it cannot
   * drift from the number that refuses an upload, with the composition payload
   * as a fallback for a rolling deploy. Both are `null` until their read lands —
   * the tiles render a dash rather than a zero, which on a money/quota screen
   * would read as a measured fact.
   */
  const storageComp = storageQuery.data ?? null
  const storageUsedBytes = storageComp
    ? storageComp.live.bytes +
      storageComp.reclaimable.bin.bytes +
      storageComp.reclaimable.versions.bytes +
      storageComp.reclaimable.quarantine.bytes
    : null
  const storageQuotaBytes =
    typeof tenantLimits?.storageQuotaBytes === 'number'
      ? tenantLimits.storageQuotaBytes
      : storageComp?.quotaBytes ?? null
  // A3: the TENANT's resolved included seats. The SERVER already resolved the
  // precedence (`tenant_settings.included_seats ?? plan.includedSeats`), so
  // `tenantLimits.includedSeats` IS the answer — read it, do not re-derive it.
  // The `plan.includedSeats` branch is NOT a second copy of that rule: it is the
  // rolling-deploy fallback for when an OLD API omits the (optional) field
  // entirely (`undefined`), the one case `tenantLimits.includedSeats` cannot
  // cover. Reading `plan.includedSeats` alone printed the PRESET's number, so a
  // per-tenant seat override set on the console never appeared here.
  const includedSeats =
    typeof tenantLimits?.includedSeats === 'number'
      ? tenantLimits.includedSeats
      : typeof plan.includedSeats === 'number'
        ? plan.includedSeats
        : null
  // "Most popular" = the highest-priced active PAID plan. The catalog carries
  // no such flag, so this is a display heuristic, not stored data — it only
  // drives the badge + the emphasized card, never any pricing or entitlement.
  const popularPlanId =
    plans
      .filter((p) => (p.basePriceMinor ?? 0) > 0)
      .reduce<PlanView | null>(
        (top, p) => (!top || (p.basePriceMinor ?? 0) > (top.basePriceMinor ?? 0) ? p : top), null)
      ?.id ?? null

  return (
    // Content dims while a manual refresh is in flight — readable throughout,
    // so the minimum-visible window costs the reader nothing.
    <div className="space-y-6" data-revalidating={String(isRefreshing)} aria-busy={isRefreshing}>
      {/* Exempt: this row holds the Refresh control whose spinner IS the
          feedback, so it must not recede along with the content. */}
      <div className="flex items-center justify-between gap-4" data-revalidate-exempt>
        <div className="min-w-0">
          <h2 className="text-lg themed-text font-medium">{t('title')}</h2>
          <p className="text-xs themed-text-muted mt-0.5">{t('subtitle')}</p>
        </div>
        {/* Spins and disables while any billing read is in flight. Without the
            feedback a click looked like a no-op whenever the cache answered
            instantly or the request was slow. */}
        {/* min-w + justify-end: the label swaps to a longer string while
            fetching, which would otherwise resize the button on every refresh. */}
        <button
          onClick={refresh}
          disabled={isRefreshing}
          className="text-xs themed-text-muted hover:themed-text flex items-center justify-end gap-1 min-w-24 transition-colors disabled:opacity-60 disabled:cursor-default"
        >
          <RefreshCw size={12} className={isRefreshing ? 'animate-spin' : undefined} />
          {isRefreshing ? t('refreshing') : t('refresh')}
        </button>
      </div>

      {/* Post-checkout mirror wait. Without this the customer sees "Free" for a
          few seconds right after paying and assumes the payment failed.

          Only mounted while syncing — it takes its own space-y-6 slot when it
          appears and gives it back when the webhook lands. */}
      {syncing && (
        <div className="p-4 rounded-xl ring-1 ring-[color:var(--bill-ring)] bg-[color:var(--bill-tint)]">
          <div className="flex items-start gap-3">
            <Loader2 size={16} className="text-[color:var(--bill-primary)] shrink-0 mt-0.5 animate-spin" />
            <div>
              <p className="text-sm themed-text font-medium">{t('checkout.finalizing')}</p>
              <p className="text-xs themed-text-muted mt-0.5">{t('checkout.finalizingBody')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Same for a credit top-up. Its own banner rather than a shared one:
          "activating your plan" is wrong copy for someone who just bought
          credit, and the two purchases can be in flight independently. */}
      {awaitingCredits && (
        <div className="p-4 rounded-xl ring-1 ring-[color:var(--bill-ring)] bg-[color:var(--bill-tint)]">
          <div className="flex items-start gap-3">
            <Loader2 size={16} className="text-[color:var(--bill-primary)] shrink-0 mt-0.5 animate-spin" />
            <div>
              <p className="text-sm themed-text font-medium">{t('credits.checkout.finalizing')}</p>
              <p className="text-xs themed-text-muted mt-0.5">{t('credits.checkout.finalizingBody')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Dunning. Deliberately the first thing on the page and not dismissible:
          AI is already blocked at this point (COST_LIMIT_PAST_DUE), so the
          customer needs the fix path, not a badge. */}
      {dunning && (
        <div className="p-4 rounded-xl ring-1 ring-red-500/30 bg-red-500/8 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm themed-text font-medium">{t('dunning.title')}</p>
              <p className="text-xs themed-text-muted mt-0.5">{t('dunning.body')}</p>
            </div>
          </div>
          {canAct && (
            <button
              onClick={() => void redirectTo(() => portalMut.mutateAsync())}
              disabled={busy === 'portal'}
              className="text-xs themed-btn-primary px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-medium"
            >
              {busy === 'portal' ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
              {t('dunning.action')}
            </button>
          )}
        </div>
      )}

      {/* ── Key information (manual mode) ───────────────────────────────
          Under manual authority a plan is an entitlement TEMPLATE, not a
          purchase the customer made, so this leads with what the tenant HAS,
          not a plan name and a price the plan does not decide (the invoiced
          figure is the per-tenant contract, set by the operator, and lives on
          the statement below). The plan NAME is deliberately absent — a
          manually-billed customer neither chose it nor can act on it.

          Free credits is the headline: the pool every AI turn draws from first,
          with this period's utilisation and a hand-off to the Spending
          breakdown. The four-tile grid beneath carries the rest; two tiles are
          links, because each has a page that owns its detail — storage (→
          Settings → Storage) and the meter itself (→ Spending). This REPLACES
          the provider hero, the billable-seat card and the standalone allowance
          meter, whose utilisation folds into the free-credits row here. */}
      {!providerBilled && (
        <section className={`${CARD} p-4 sm:p-6 relative overflow-hidden space-y-6`} data-billing-keyinfo-card>
          <div
            className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[color:var(--bill-tint)] blur-3xl"
            aria-hidden
          />
          {/* Free credits — the headline pool. The whole block is a button when
              a Spending destination exists (the meter is a summary; the detail
              lives there), and a static block otherwise. */}
          <div
            className={`relative -m-1.5 p-1.5 rounded-xl ${
              onOpenBudgets
                ? 'cursor-pointer transition-colors hover:bg-[color:var(--bill-tint)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bill-ring)]'
                : ''
            }`}
            role={onOpenBudgets ? 'button' : undefined}
            tabIndex={onOpenBudgets ? 0 : undefined}
            onClick={onOpenBudgets}
            onKeyDown={
              onOpenBudgets
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onOpenBudgets()
                    }
                  }
                : undefined
            }
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--bill-primary)] flex items-center gap-1.5">
                <Sparkles size={12} className="shrink-0" />
                {t('keyInfo.freeCredits', { defaultValue: 'Free credits' })}
              </p>
              {onOpenBudgets && (
                <span className="flex items-center gap-0.5 text-[11px] font-medium text-[color:var(--bill-primary)] shrink-0">
                  {t('usage.viewDetailed')}
                  <ChevronRight size={13} />
                </span>
              )}
            </div>
            <div className="flex items-end justify-between gap-3 mt-1">
              <span className={`text-3xl sm:text-4xl themed-text font-semibold tracking-tight ${NUMERIC}`}>
                {formatBillingMicro(includedUsed, currency)}
                {allowance != null && allowance > 0 && (
                  <span className="text-lg themed-text-muted font-normal">
                    {' / '}
                    {formatBillingMicro(allowance, currency)}
                  </span>
                )}
              </span>
              <span className={`text-[11px] themed-text-muted ${NUMERIC} pb-1`}>
                {t('usage.periodWindow', {
                  defaultValue: '{{from}} – {{to}}',
                  from: fmtDate(usage.periodStart),
                  to: fmtDate(usage.periodEnd),
                })}
              </span>
            </div>
            {allowance != null && allowance > 0 ? (
              <div className="h-2 rounded-full themed-bg-tertiary overflow-hidden mt-2">
                <div
                  className={`h-full transition-all duration-500 motion-reduce:transition-none ${meterTone(
                    meterPct(includedUsed, allowance),
                  )}`}
                  style={{ width: `${meterPct(includedUsed, allowance)}%` }}
                />
              </div>
            ) : (
              <p className="text-[11px] themed-text-muted mt-2">{t('usage.noAllowance')}</p>
            )}
            {/* Spend funded by pools OUTSIDE the included allowance — promo,
                prepaid credit, platform grace. Without it a tenant running
                entirely on credits reads "used 0" with no account of its turns. */}
            {otherFunding.length > 0 && (
              <p className="text-[11px] themed-text-muted mt-1.5" title={t('usage.otherFundingHint')}>
                {t('usage.otherFunding')}: {otherFunding.join(' · ')}
              </p>
            )}
            {showMembersClockNote && (
              <p className="text-[11px] themed-text-muted mt-1.5">
                {t('usage.otherClock', {
                  defaultValue:
                    'Members’ own allowances run on the calendar month instead, so the budget page shows a different total.',
                })}
              </p>
            )}
          </div>

          {/* The other four facts. Storage is a link (→ Settings → Storage);
              the rest are read-only figures. Every value renders a dash until
              its read lands rather than a zero, which would read as measured. */}
          <dl className="relative grid grid-cols-2 sm:grid-cols-3 gap-3 pt-5 border-t themed-border">
            {/* Storage — used against quota, with a thin utilisation bar. */}
            {(() => {
              const quotaLabel = storageQuotaBytes != null ? formatStorage(storageQuotaBytes) : null
              const usedLabel =
                storageUsedBytes == null ? null : formatStorage(storageUsedBytes) ?? '0 B'
              const clickable = !!onOpenStorage && quotaLabel != null
              return (
                <div
                  className={`rounded-lg -m-1 p-1 ${
                    clickable
                      ? 'cursor-pointer transition-colors hover:bg-[color:var(--bill-tint)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bill-ring)]'
                      : ''
                  }`}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? onOpenStorage : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onOpenStorage!()
                          }
                        }
                      : undefined
                  }
                >
                  <dt className="text-[11px] themed-text-muted uppercase tracking-wide flex items-center gap-1">
                    <HardDrive size={11} className="shrink-0" />
                    {t('current.storage')}
                    <InfoHint text={t('current.storageHint')} />
                    {clickable && <ChevronRight size={11} className="themed-text-muted ml-auto" />}
                  </dt>
                  <dd className={`text-sm themed-text font-medium mt-0.5 ${NUMERIC}`}>
                    {quotaLabel == null ? '—' : `${usedLabel ?? '—'} / ${quotaLabel}`}
                  </dd>
                  {quotaLabel != null && storageUsedBytes != null && storageQuotaBytes! > 0 && (
                    <div className="h-1 rounded-full themed-bg-tertiary overflow-hidden mt-1">
                      <div
                        className={`h-full ${meterTone(meterPct(storageUsedBytes, storageQuotaBytes!))}`}
                        style={{ width: `${meterPct(storageUsedBytes, storageQuotaBytes!)}%` }}
                      />
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Included seats — under manual billing this is also the seat
                ceiling. Links to the members page (where seats are managed);
                a static figure on a personal tenant, which has none. */}
            {(() => {
              const clickable = !!onOpenMembers
              return (
                <div
                  className={`rounded-lg -m-1 p-1 ${
                    clickable
                      ? 'cursor-pointer transition-colors hover:bg-[color:var(--bill-tint)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bill-ring)]'
                      : ''
                  }`}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? onOpenMembers : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onOpenMembers!()
                          }
                        }
                      : undefined
                  }
                >
                  <dt className="text-[11px] themed-text-muted uppercase tracking-wide flex items-center gap-1">
                    <CheckCircle2 size={11} className="shrink-0" />
                    {t('keyInfo.includedSeats', { defaultValue: 'Included seats' })}
                    <InfoHint text={t('keyInfo.includedSeatsHint', {
                      defaultValue: 'Under manual billing this is also your seat ceiling.',
                    })} />
                    {clickable && <ChevronRight size={11} className="themed-text-muted ml-auto" />}
                  </dt>
                  <dd className={`text-sm themed-text font-medium mt-0.5 ${NUMERIC}`}>
                    {/* SEATS USED / SEATS INCLUDED — the same "used / total" shape
                        as the storage tile above (`872.3 KB / 10 GB`), so an
                        admin reads occupancy at a glance rather than a bare
                        ceiling. Only when the plan actually INCLUDES seats
                        (> 0): a free plan includes 0 (every free org), where
                        "N / 0" reads as a broken ratio — and the headcount is
                        NOT the included count, so show the truthful "0" rather
                        than mislabelling the member count as "included". `null`
                        (no seat concept) shows "—", matching the storage tile's
                        own null render — never the live headcount, which would
                        read as an ever-climbing "included" allowance. */}
                    {includedSeats != null && includedSeats > 0
                      ? `${seats.billable} / ${includedSeats}`
                      : (includedSeats ?? '—')}
                  </dd>
                </div>
              )
            })()}

            {/* Agent tasks — a per-USER concurrency limit; the tooltip says so. */}
            <div>
              <dt className="text-[11px] themed-text-muted uppercase tracking-wide flex items-center gap-1">
                <Users size={11} className="shrink-0" />
                {t('current.agents')}
                <InfoHint text={t('current.agentsHint')} />
              </dt>
              <dd className={`text-sm themed-text font-medium mt-0.5 ${NUMERIC}`}>
                {typeof tenantLimits?.agentConcurrencyCap === 'number'
                  ? tenantLimits.agentConcurrencyCap
                  : '—'}
              </dd>
            </div>

            {/* Extra credits (prepaid top-ups) intentionally omitted here — it
                has its own dedicated Usage-credits card below, and repeating the
                balance in this key-info grid was redundant (2026-08-19). */}
          </dl>
        </section>
      )}

      {/* Current plan — the hero. This is the one fact the page exists to state,
          so it gets the largest type on screen and a 2:1 grid: the plan itself
          on the left, the billable-seat count as its own stat card on the
          right (the redesign's "billable seats" panel, minus the fabricated
          add-seats CTA — seat count is derived, not something we let the user
          set here). PROVIDER MODE ONLY — the manual key-info card above replaces
          it, the seat card and the meter. */}
      {providerBilled && (
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`${CARD} lg:col-span-2 p-4 sm:p-6 relative overflow-hidden space-y-5`}>
          {/* Atmospheric accent bloom in the corner — pure decoration, echoes
              the design's glow without carrying any state. */}
          <div
            className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[color:var(--bill-tint)] blur-3xl"
            aria-hidden
          />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--bill-primary)]">
                {t('current.heading')}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-3xl sm:text-4xl themed-text font-semibold tracking-tight">{plan.name}</span>
                {subscription && <StatusPill status={subscription.status} label={t(`status.${subscription.status}`, subscription.status)} />}
              </div>
            </div>
            {/* Price as a figure, not a sentence: the amount reads at a glance
                and the unit sits under it in muted text.

                THREE cases, not two. A zero-priced plan means different things
                on the two kinds of deployment, and printing one figure for both
                is how this page came to tell a paying customer they pay nothing:

                  provider + price  → the price
                  provider + zero   → genuinely free; "$0 / mo"
                  manual  + zero    → the PRESET sets no price; the tenant's own
                                      override does, and it is not on this page.

                Under manual authority a preset price of 0 says "the preset does
                not decide this", not "you owe nothing" — so no figure is shown
                at all, and the muted line says the arrangement is elsewhere.
                A number here would be a confident, wrong answer to the one
                question this page exists to answer. (The real figure needs the
                paid-through/amount-due statement — B3 in the plan, deferred.) */}
            <div className="text-right shrink-0">
              {/* This block is PROVIDER-ONLY — the whole hero is gated on
                  `providerBilled`. A manual deployment never reaches it (its
                  invoiced figure is the per-tenant contract on the statement,
                  and the key-info card carries what the tenant has instead), so
                  the old billed-by-arrangement branch is gone with the hero. */}
              {plan.basePriceMinor != null && plan.basePriceMinor > 0 ? (
                <>
                  <div className={`text-3xl sm:text-4xl themed-text font-semibold tracking-tight ${NUMERIC}`}>
                    {formatCurrencyMinor(plan.basePriceMinor ?? 0, currency)}
                  </div>
                  <div className="text-xs themed-text-muted mt-0.5">{t('current.perSeatUnit')}</div>
                </>
              ) : (
                <>
                  {/* Formatted, not the literal "$0" this used to print: the
                      symbol has to follow the plan's own currency, or a ringgit
                      deployment renders a dollar sign (whitelabel rule). */}
                  <div className={`text-3xl sm:text-4xl themed-text font-semibold tracking-tight ${NUMERIC}`}>
                    {formatCurrencyMinor(0, currency)}
                  </div>
                  <div className="text-xs themed-text-muted mt-0.5">{t('current.perMonthUnit')}</div>
                </>
              )}
            </div>
          </div>

          {/* ── What the plan provides, and when it renews ─────────────────
              The billing period is absent on Free (no subscription row), but
              storage and concurrency are NOT — every tenant has both, and a
              free tenant asking "what do I actually get" is exactly who this
              card is for. So the list always renders and only the period is
              conditional.

              ── These are the TENANT's figures, never `plan.*` ─────────────
              `plan.storageQuotaBytes` / `plan.agentConcurrencyCap` are on the
              wire and are the wrong source here: they say what the PLAN
              defines, which diverges from what this tenant HAS whenever an
              operator has customised the tenant or a plan row was edited
              without propagation. `tenant.*` carries the columns the enforcers
              actually read, so a number shown here cannot drift from the one
              that stops an upload or refuses a task.

              Optional on the type for a rolling deploy — an older API omits the
              object, and a card that renders "0 GB" during a restart would be
              worse than one that briefly renders nothing. */}
          {/* The whole list is conditional, not just its rows: every child here
              is optional, so on a free tenant served by a pre-`tenant` API the
              container would paint a bare `border-t` divider with nothing under
              it. A rule that separates nothing reads as a rendering fault. */}
          {(subscription || storageLabel || typeof tenantLimits?.agentConcurrencyCap === 'number') && (
          <dl className="relative grid grid-cols-2 gap-x-4 gap-y-4 pt-5 border-t themed-border">
            {subscription && (
              <div className="col-span-2">
                <dt className="text-[11px] themed-text-muted uppercase tracking-wide">{t('current.period')}</dt>
                <dd className={`text-sm themed-text font-medium mt-0.5 ${NUMERIC}`}>
                  {fmtDate(subscription.currentPeriodStart)} – {fmtDate(subscription.currentPeriodEnd)}
                </dd>
              </div>
            )}
            {storageLabel && (
              <div>
                <dt className="text-[11px] themed-text-muted uppercase tracking-wide">{t('current.storage')}</dt>
                <dd className={`text-sm themed-text font-medium mt-0.5 ${NUMERIC}`}>{storageLabel}</dd>
                {/* NAMED, not linked. The breakdown (live + bin + versions +
                    quarantine) belongs to Settings → Storage, which already
                    owns it and is already membership-gated — "a duplicated
                    figure is one that can drift". Naming the screen is the same
                    move the allowance meter makes about the budget dashboard
                    two blocks below, and it needs no new prop threaded through
                    SettingsView. */}
                <dd className="text-[11px] themed-text-muted mt-0.5">{t('current.storageHint')}</dd>
              </div>
            )}
            {/* PER PERSON, not per org — and the hint has to say so.
                `checkAgentConcurrencyCap` counts `countUserPendingJobs(userId)`,
                which filters the queue on the job's OWN user, so three members
                each get this many rather than sharing it. The column lives on
                the tenant, which makes this a per-tenant SETTING of a per-user
                LIMIT — and it renders inches from a storage figure that really
                is org-wide, so an ambiguous hint reads as the wrong one.
                (First wording here said "how many the assistant can run in
                parallel", which is the org-pool reading.) */}
            {typeof tenantLimits?.agentConcurrencyCap === 'number' && (
              <div>
                <dt className="text-[11px] themed-text-muted uppercase tracking-wide">{t('current.agents')}</dt>
                <dd className={`text-sm themed-text font-medium mt-0.5 ${NUMERIC}`}>
                  {tenantLimits.agentConcurrencyCap}
                </dd>
                <dd className="text-[11px] themed-text-muted mt-0.5">{t('current.agentsHint')}</dd>
              </div>
            )}
          </dl>
          )}
          {/* AI budget for this period — the consumed allowance meter AND the
              overage it will bill beyond that, together in the hero. The design
              has no separate usage card, so both charge-facing figures live
              here: "included" is metering, "overage" is the charge, each
              labelled so the two aren't read as one number. This meter is the
              TENANT CREDIT POOL, so it tracks the billing period shown above.
              Individual members' quotas reset on the calendar month instead —
              that axis lives on the budget dashboard, not here.

              ── That last sentence used to live ONLY here (A2) ─────────────
              Which meant this page and the budget dashboard printed two
              different totals for "what did we spend on AI" — both correct,
              on two different clocks — and the only thing reconciling them was
              this comment. A reader comparing the two screens concluded we
              could not count. The window is now NAMED on the meter and the
              other screen is pointed at by name.

              The window comes from `usage.periodStart/End`, NOT from the
              subscription: a free tenant has no subscription row but does have
              a metering window, and sourcing it from the subscription would
              leave exactly those tenants with an unlabelled meter.

              What is deliberately NOT done: printing the calendar-month figure
              here too. `features/billing` does not depend on `@papan/orgs`
              (the arrow runs the other way), so fetching it would need a new
              endpoint or a layering break — and a duplicated number is a
              number that can drift. Naming the clocks costs nothing and cannot
              disagree with itself. */}
          {/* Exactly one of these can show: scheduledCancel and pendingPlan are
              derived from the same mutually-exclusive pair above. They used to
              test cancelAtPeriodEnd / pendingPlanId independently, with the
              OPPOSITE precedence to the plan picker — so a subscription with
              both flags set disagreed with itself on the same screen. */}
          {scheduledCancel && (
            <div className="relative flex items-start gap-2 text-xs text-amber-400 bg-amber-500/8 ring-1 ring-amber-500/20 rounded-lg px-3 py-2">
              <CalendarClock size={14} className="shrink-0 mt-px" />
              <span>{t('current.cancelsOn', { date: fmtDate(subscription?.currentPeriodEnd) })}</span>
            </div>
          )}
          {/* A queued downgrade. The subscription still reports the CURRENT
              plan until the boundary, so without this the customer sees no
              evidence of the change they just made — not even after a refresh. */}
          {pendingPlan && (
            <div className="relative flex items-start gap-2 text-xs text-amber-400 bg-amber-500/8 ring-1 ring-amber-500/20 rounded-lg px-3 py-2">
              <CalendarClock size={14} className="shrink-0 mt-px" />
              <span>
                {t('current.changesOn', {
                  plan: pendingPlan.name,
                  date: fmtDate(subscription?.currentPeriodEnd),
                })}
              </span>
            </div>
          )}
        </div>

        {/* Billable seats — the design's centred stat card. The "Add seats"
            action opens the members page; it's only rendered when the caller
            wires `onManageSeats` (org tenant + admin), so a personal tenant
            simply shows the count with no button. */}
        <div className={`${CARD} p-6 flex flex-col items-center justify-center text-center gap-4`}>
          <div className="w-14 h-14 rounded-2xl bg-[color:var(--bill-tint)] text-[color:var(--bill-primary)] flex items-center justify-center">
            <Users size={26} />
          </div>
          <div>
            <p className="text-[11px] themed-text-muted uppercase tracking-wide">{t('current.seats')}</p>
            {/* `<billable> / <max>`, or the bare count when there is no ceiling.
                NEVER the word "unlimited" rendered where a number goes.

                The no-denominator branch is the PRIMARY case, not a fallback:
                `max_seats` is null on every plan until Gate 3's final step sets
                it, so this is what actually renders today. The `/ N` half is
                what appears the moment an operator types a number into the
                price book. */}
            <p className={`text-4xl themed-text font-semibold tracking-tight mt-1 ${NUMERIC}`}>
              {seats.billable}
              {typeof seats.max === 'number' && (
                <span className="themed-text-muted font-normal"> / {seats.max}</span>
              )}
            </p>
            {/* Over the ceiling: possible without anything being broken — the
                first owner is seated exemptly, and a cap can be lowered under
                an org that already exceeds it. Say so rather than letting
                "3 / 2" read as a rendering fault. */}
            {typeof seats.max === 'number' && seats.billable > seats.max && (
              <p className="text-[11px] text-amber-400 mt-1">{t('current.seatsOver')}</p>
            )}
          </div>
          {onManageSeats && (
            <button
              onClick={onManageSeats}
              className="w-full py-2.5 rounded-xl border themed-border text-sm font-medium themed-text hover:themed-bg-tertiary transition-colors active:scale-[0.98]"
            >
              {t('current.addSeats')}
            </button>
          )}
        </div>
      </section>
      )}

      {/* ── What this period has used (S4) ──────────────────────────────
          Lifted out of the plan hero, which was carrying two subjects at once:
          WHAT I PAY FOR (contractual, and the fact this page exists to state)
          and WHAT I HAVE USED (metering, which moves daily). Eleven distinct
          facts in one card made the headline read as a preamble to a bar.

          Nothing inside changed source or gating — this is the same markup,
          one level shallower, in a card of its own. PROVIDER MODE ONLY — under
          manual billing the key-info card above carries this utilisation. */}
      {providerBilled && (
      <section className={`${CARD} p-4 sm:p-6`}>
          {/* ── the allowance meter starts its own card here (S4) ────────
            The hero carried eleven distinct facts: plan, status, price,
            period, the meter, its window, its converted equivalent, the
            other-funding line, the other-clock line, the overage row and the
            detail link — plus up to two scheduled-change banners. That is
            two subjects wearing one card: WHAT I PAY FOR (contractual, and
            the thing this page exists to state) and WHAT I HAVE USED
            (metering, which moves daily).

            Split so the headline fact keeps the largest type on screen and
            is not read as a preamble to a bar chart. Nothing here changed
            its source or its gating. */}
        <div className="relative pt-5 border-t themed-border space-y-3" data-billing-usage-card>
          <div className="space-y-2">
            <div className="flex items-end justify-between gap-3">
              <span className="text-sm themed-text-secondary font-medium">
                {t('usage.included')}
                {/* The clock this figure runs on, stated ON the figure. */}
                <span className={`block text-[11px] font-normal themed-text-muted ${NUMERIC}`}>
                  {t('usage.periodWindow', {
                    defaultValue: '{{from}} – {{to}}',
                    from: fmtDate(usage.periodStart),
                    to: fmtDate(usage.periodEnd),
                  })}
                </span>
              </span>
              <span className={`text-sm themed-text ${NUMERIC}`}>
                {formatBillingMicro(includedUsed, currency)}
                {allowance != null && allowance > 0
                  ? ` / ${formatBillingMicro(allowance, currency)}`
                  : ''}
              </span>
            </div>
            <div className="h-2 rounded-full themed-bg-tertiary overflow-hidden">
              <div
                className={`h-full transition-all duration-500 motion-reduce:transition-none ${meterTone(
                  meterPct(includedUsed, allowance ?? 0),
                )}`}
                style={{ width: `${meterPct(includedUsed, allowance ?? 0)}%` }}
              />
            </div>
            {/* The allowance figure needs no second rendering: it is already in
                the currency the customer is charged in. This used to print a
                charged-currency "approximately" line beneath a USD one, derived
                at the plan's contractual overage rate — two numbers for one
                fact, and the rate that produced it is retired. */}
            {allowance == null || allowance <= 0 ? (
              <p className="text-[11px] themed-text-muted">{t('usage.noAllowance')}</p>
            ) : null}
            {/* Spend funded by a pool OUTSIDE the included allowance — promo,
                prepaid usage credits, platform-absorbed grace. The meter above
                shows only the `free` bucket, so without this the page silently
                omitted whole categories of real usage: a tenant running
                entirely on credits saw "$0.00 used" and no account of where
                their turns went. None of it is billable, hence separate from
                the overage row rather than folded into it. */}
            {otherFunding.length > 0 && (
              <p className="text-[11px] themed-text-muted" title={t('usage.otherFundingHint')}>
                {t('usage.otherFunding')}: {otherFunding.join(' · ')}
              </p>
            )}
            {/* Where the OTHER total lives, and why it differs.
                Gated on `showMembersClockNote`, NOT on `onOpenBudgets`.

                This note used to hang off `onOpenBudgets`, justified by a
                claim about the hosts that had stopped being true: they were
                said to pass it as `!isPersonal && <has access>`, and in fact
                both passed a ternary that was ALWAYS a function. So a
                personal drive — one person, no members page — was told that
                "members' own allowances" run on a different clock.

                Gating it on `!isPersonal` via `onOpenBudgets` would have
                fixed the sentence by removing the hand-off button below,
                which is perfectly correct on a personal drive (it has its own
                Spending page). The sentence is about whether MEMBERS exist;
                the button is about where a reader should be sent. Two
                questions, two props. */}
            {showMembersClockNote && (
              <p className="text-[11px] themed-text-muted">
                {t('usage.otherClock', {
                  defaultValue:
                    'Members’ own allowances run on the calendar month instead, so the budget page shows a different total.',
                })}
              </p>
            )}
          </div>

          {/* The jump to the detailed usage breakdown.
             *
             * It used to lead with "Overage: <amount>". POSTPAID OVERAGE IS
             * RETIRED (Gate 1) — no plan sells it, `extraCapBillingMicro`
             * resolves to 0 for every tenant, and the rate that converted it is
             * gone — so the figure could only ever have read zero. A permanent
             * zero on a money screen is worse than nothing: it reads as a
             * measured fact. */}
          <div className="flex items-center justify-between gap-3 pt-3 border-t themed-border">
            <span className="flex items-center gap-2 text-sm themed-text-secondary min-w-0">
              <Info size={15} className="themed-text-muted shrink-0" />
              <span className="truncate">{t('usage.estimateNote')}</span>
            </span>
            {/* The detailed breakdown lives on another page — this is the
                hand-off. Hidden when the caller can't offer one. */}
            {onOpenBudgets && (
              <button
                onClick={onOpenBudgets}
                className="text-xs font-medium text-[color:var(--bill-primary)] hover:underline shrink-0"
              >
                {t('usage.viewDetailed')}
              </button>
            )}
          </div>
        </div>
      </section>
      )}

      {/* Plans — the design's bento cards. Descriptions and feature bullets are
          DERIVED from real catalog fields (included budget, overage rate,
          storage, keybook) — never fabricated marketing — so a card never
          claims something the plan doesn't grant. "Most popular" is a display
          heuristic (highest-priced paid plan), emphasized with a badge + glow.
          The card the customer is ON is ringed so they can find themselves. */}
      {canAct && providerBilled && (
        <section>
          <div>
            <h3 className={`${SECTION_LABEL} block`}>{t('planPicker.heading')}</h3>
            <p className="text-sm themed-text-muted mt-1">{t('planPicker.subheading')}</p>
          </div>
          {/* mt-6 leaves clearance for the "most popular" chip that overhangs
              the top edge of the first-row cards. */}
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {plans.map((p) => {
              const isCurrent = p.id === plan.id
              // Selecting Free runs the CANCEL flow, not change-plan — there is
              // no Stripe price to switch to. Several bits of card state have to
              // follow that redirection rather than assume plan semantics.
              //
              // A zero price is read as "this card IS Free", and that reading is
              // only sound because `GET /plans` returns SELECTABLE plans: it
              // drops every zero-priced plan that is not the free one, precisely
              // so this test cannot mis-fire. It DID mis-fire between the
              // 2026-08-10 catalog consolidation and B8 — priceless `Default`
              // reached the picker, and "Select Default" ran `cancel()`,
              // dropping a paying customer to Free instead. If that filter is
              // ever relaxed, this must become an identity test, not a price
              // one. See the invariant note on the route.
              // The list route drops every unpriced plan, so `null` cannot
              // reach here — but treating it as free if it ever did is the one
              // reading that turns "Select" into "Cancel".
              const isFree = p.basePriceMinor === 0
              // Already queued for the period boundary. Re-selecting it used to
              // re-run the whole flow and re-toast "changes at period end",
              // making a no-op look like a fresh action every click.
              const isScheduled = isFree
                // Cancelling IS the scheduled move to Free.
                ? scheduledCancel
                : scheduledPlanId === p.id
              /**
               * Selecting Free means CANCEL, and with no live subscription
               * there is nothing to cancel — `changePlan` returns immediately.
               *
               * Its inline comment used to justify that early return with
               * "already on Free; the button is disabled anyway", which assumed
               * *seeing a Free card without a subscription* implies *being on
               * Free*. `platform_plan_override` broke that: an operator can put
               * a tenant on a non-free plan with no subscription, and then
               * `isCurrent` is false, nothing locks the card, and "Select Free"
               * is an enabled button that does nothing at all.
               *
               * So the lock states the condition rather than assuming it, and
               * the label says why instead of leaving a greyed "Select Free"
               * advertising an action that cannot happen.
               *
               * Deliberately NOT `&& !isCurrent`: this says what is TRUE (there
               * is no subscription to cancel), and a tenant already on Free
               * satisfies it too. Which label wins is a separate question, and
               * it is answered where the labels are chosen — `isCurrent` is
               * tested first there, exactly as it already is for `isScheduled`.
               * Folding the precedence in here would duplicate that ordering in
               * a second place, where nothing can hold the two in step.
               */
              const nothingToCancel = isFree && !subscription
              const locked = isCurrent || isScheduled || nothingToCancel
              // `busy` carries the planId for change-plan/subscribe but the
              // literal 'cancel' for the cancel mutation, so the Free card would
              // never match `busy === p.id` — it sat inert while the Cancel
              // button below it spun for the very same request.
              const rowBusy = busy === p.id || (isFree && busy === 'cancel')
              const isPopular = p.id === popularPlanId
              const tagline = t(`planPicker.tagline.${p.id}`, { defaultValue: '' })

              // Bullets straight from the plan's own fields — order: allowance,
              // agents, storage, keybook. Nothing here is copy we invented.
              //
              // The OVERAGE bullet is gone, along with postpaid overage itself
              // (Gate 1). This comment still listed it until 2026-08-18, and
              // `planPicker.feat.overage` still existed in all four locales
              // with no reader — a dead key the next person would have found
              // and trusted. Both are deleted.
              const bullets: string[] = []
              // The allowance a card advertises is the FLAT plan pool — the same
              // value `applyEntitlements` writes into the ceiling. The per-seat
              // `extra_seat_allowance` term is retired (folded into `base_pool`,
              // 0 everywhere), so the pool is the base alone; keeping the
              // seat-scaled term here would label a now-always-0 field.
              const planAllowance = p.basePoolBillingMicro ?? 0
              bullets.push(
                planAllowance > 0
                  ? t('planPicker.feat.budget', { amount: formatBillingMicro(planAllowance, currency) })
                  : t('planPicker.feat.noBudget'),
              )
              // M4 — the concurrency entitlement was granted by the plan,
              // enforced by a 429, and named on no customer-facing screen.
              // Bullets here are derived from real catalog fields, so this is
              // one more of them rather than invented copy.
              if (typeof p.agentConcurrencyCap === 'number' && p.agentConcurrencyCap > 0) {
                bullets.push(t('planPicker.feat.agents', { count: p.agentConcurrencyCap }))
              }
              const storage = formatStorage(p.storageQuotaBytes)
              if (storage) bullets.push(t('planPicker.feat.storage', { amount: storage }))
              if (p.features?.keybook) bullets.push(t('planPicker.feat.keybook'))

              return (
                <li
                  key={p.id}
                  // Current vs most-popular get DISTINCT rings so the two are
                  // never confused: current = emerald (the plan you're ON,
                  // matching the active StatusPill), most-popular = the design's
                  // lavender accent. isCurrent wins when a plan is both.
                  className={`${CARD} relative flex flex-col p-4 ${
                    isCurrent
                      ? 'ring-2 ring-emerald-500/50'
                      : isPopular
                      ? 'ring-2 ring-[#ddb8ff]'
                      : ''
                  }`}
                >
                  {/* "Most popular" chip — solid, anchored to the top edge of
                      the card (new-design style). Suppressed when this is also
                      the current plan, which owns the top-right badge instead. */}
                  {isPopular && !isCurrent && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-[0.08em] px-3 py-1 rounded-full bg-[#ddb8ff] text-[#490080] shadow-[0_4px_12px_-2px_rgba(147,51,234,0.5)] whitespace-nowrap">
                      {t('planPicker.mostPopular')}
                    </span>
                  )}

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-base themed-text font-semibold">{p.name}</span>
                    {isCurrent && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-500/12 text-emerald-400 ring-1 ring-emerald-500/25">
                        {t('planPicker.current')}
                      </span>
                    )}
                  </div>

                  {/* Description — reserves a fixed height so every card's price
                      lines up whether or not it has a tagline. */}
                  <p className="text-xs themed-text-muted mt-2 min-h-10">{tagline}</p>

                  {/* Price as a figure with the unit beneath it; Free reads as
                      a formatted zero + "/ month" rather than repeating the
                      word. The zero goes through `formatCurrencyMinor` like the
                      hero's, and for the same reason: a literal "$0" prints a
                      dollar sign on a ringgit deployment. This card only renders
                      under provider authority, so "free" is the honest reading
                      of a zero here — unlike the hero, which must also serve the
                      manual case where zero means "the preset sets no price". */}
                  <div className="mt-4">
                    {(p.basePriceMinor ?? 0) > 0 ? (
                      <>
                        <span className={`text-2xl themed-text font-semibold tracking-tight ${NUMERIC}`}>
                          {formatCurrencyMinor(p.basePriceMinor ?? 0, currency)}
                        </span>
                        <span className="text-xs themed-text-muted ml-1">{t('current.perSeatUnit')}</span>
                      </>
                    ) : (
                      <>
                        <span className={`text-2xl themed-text font-semibold tracking-tight ${NUMERIC}`}>
                          {formatCurrencyMinor(0, p.currency ?? FALLBACK_CURRENCY)}
                        </span>
                        <span className="text-xs themed-text-muted ml-1">{t('current.perMonthUnit')}</span>
                      </>
                    )}
                  </div>

                  {/* Included-budget eyebrow, in the design's lavender accent. */}
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--bill-primary)] mt-2">
                    {planAllowance > 0
                      ? t('planPicker.includesBudget', { amount: formatBillingMicro(planAllowance, currency) })
                      : t('planPicker.foreverFree')}
                  </p>

                  {/* Feature checklist. flex-1 pushes the CTA to the card floor
                      so buttons align across cards of differing bullet counts. */}
                  <ul className="space-y-3 mt-6 mb-7 flex-1">
                    {bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-xs themed-text-secondary">
                        <CheckCircle2 size={15} className="text-[color:var(--bill-primary)] shrink-0 mt-px" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Full-width CTA. Popular gets the design's purple→blue
                      gradient; other actionable plans an accent outline; the
                      current/scheduled plan a muted, inert fill. Centring holds
                      the label steady as it cycles through its states. */}
                  <button
                    onClick={() => void changePlan(p)}
                    disabled={locked || busy !== null}
                    className={`w-full py-3.5 rounded-xl text-sm font-medium flex items-center justify-center transition-all active:scale-[0.98] disabled:active:scale-100 ${
                      locked
                        ? 'themed-bg-tertiary themed-text-muted cursor-default'
                        : isPopular
                        ? 'bg-gradient-to-r from-[#ddb8ff] to-[#0566d9] text-white hover:brightness-110'
                        : 'ring-1 ring-[color:var(--bill-ring)] text-[color:var(--bill-primary)] hover:bg-[color:var(--bill-tint)]'
                    }`}
                  >
                    {rowBusy
                      ? <Loader2 size={14} className="animate-spin" />
                      : isCurrent ? t('planPicker.current')
                      : isScheduled ? t('planPicker.scheduled')
                      : nothingToCancel ? t('planPicker.nothingToCancel')
                      : isPopular ? t('planPicker.upgradeNamed', { plan: p.name })
                      : t('planPicker.selectNamed', { plan: p.name })}
                  </button>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {/* Usage credits (C5) — the prepaid balance. Below the plan picker
          because it only matters once the included allowance is understood,
          and above payment/invoices because it is a live balance rather than a
          record of past charges.

          Rendered for MEMBERS too, read-only: with overage dropped, a member
          whose turn just 402'd is told to "contact an admin", and seeing the
          balance is $0 lets them say something useful instead of reporting a
          mystery error. `canAct` gates only the buy/settings controls. */}
      <CreditsSection canAct={canAct} awaiting={awaitingCredits} onSettled={creditsSettled} />

      {/* Live promotions (M5). Above the statement and below credits: a promo
          is drawn BEFORE the included allowance, so it belongs with the other
          funding the customer has rather than with what they owe. Renders
          nothing when there is nothing. */}
      <ActivePromoSection currency={currency} />

      {/* ── The manual statement (B3) ────────────────────────────────────
          What this customer owes and until when, on the page that until now
          offered them a Stripe-shaped world instead. Renders only where an
          operator decides plans; `applicable` comes from the server so the
          client cannot claim a statement the deployment does not mean.

          Two audiences (D2): the DATE goes to everyone, because it explains a
          write freeze they can already observe. The MONEY is present only when
          the server judged the caller may see it — and it is OMITTED rather
          than zeroed, so an absent figure can never be misread as "you owe
          nothing", which a 0 would be. */}
      {statement?.applicable && (
        <section className={`${CARD} p-5 space-y-3`}>
          <h3 className={`${SECTION_LABEL} flex items-center gap-1.5`}>
            <CalendarClock size={12} />
            {t('manual.heading')}
          </h3>

          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm themed-text">
                {statement.status === 'untracked'
                  ? t('manual.untracked')
                  : statement.status === 'overdue'
                    ? t('manual.overdue', {
                        date: fmtDate(statement.paidThrough ?? null),
                        days: statement.daysOverdue ?? 0,
                      })
                    : t('manual.paidThrough', { date: fmtDate(statement.paidThrough ?? null) })}
              </p>
              <p className="themed-text-muted text-xs mt-1">{t('manual.body')}</p>
            </div>

            {/* A CONFIGURED figure only.
                `source: 'preset-seat'` with a zero amount means nothing was set
                for this tenant and the chain fell through to a priceless preset
                — which is the normal state on a deployment whose preset carries
                no price (D3). Rendering that as "MYR 0.00" tells a customer who
                IS invoiced that they owe nothing, which is precisely the claim
                B4 removed from the hero two inches above. So the unconfigured
                case says so instead.

                Zero is still shown when it was CHOSEN — a `tenant-flat` of 0 is
                a real free arrangement, and suppressing it would hide a
                deliberate decision. */}
            {statement.amountDue && (
              statement.amountDue.amountMinor === 0 && statement.amountDue.source === 'preset-seat' ? (
                <p className="themed-text-muted text-xs shrink-0 max-w-[14rem] text-right">
                  {t('manual.amountUnset')}
                </p>
              ) : (
              <div className="text-right shrink-0">
                <div className={`text-2xl themed-text font-semibold tracking-tight ${NUMERIC}`}>
                  {formatCurrencyMinor(statement.amountDue.amountMinor, statement.amountDue.currency)}
                </div>
                {/* The multiplicand, stated. A seat-priced arrangement is the
                    figure most likely to be queried, and "for N people" is the
                    answer — without it the customer has to guess whether a
                    departed colleague was counted. */}
                <div className="text-xs themed-text-muted mt-0.5">
                  {statement.amountDue.source === 'tenant-flat'
                    ? t('manual.amountFlat')
                    : t('manual.amountSeats', { count: statement.amountDue.memberCount })}
                </div>
              </div>
              )
            )}
          </div>

          {/* The operator's own words, last: a bespoke arrangement usually has a
              reason, and it explains the figure above better than a rule name. */}
          {statement.note && (
            <p className="themed-text-secondary text-xs whitespace-pre-wrap border-t themed-border pt-3">
              {statement.note}
            </p>
          )}
        </section>
      )}


      {/* Payment method + invoice history — side by side on wide screens (the
          revised design's 2-up management row). Payment method only exists once
          there's a subscription; when there isn't, invoices take the full width
          (the grid stays single-column). */}
      <section className={`grid grid-cols-1 gap-4 items-start ${canAct && subscription ? 'xl:grid-cols-2' : ''}`}>
        {/* Payment method — a clickable card row (opens the Stripe-hosted
            portal, where all card edits happen; the card on file is read live
            from Stripe, brand/last4/expiry only). */}
        {canAct && subscription && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className={`${SECTION_LABEL} flex items-center gap-1.5`}>
                <CreditCard size={12} />
                {t('payment.heading')}
              </h3>
              <button
                onClick={() => void redirectTo(() => portalMut.mutateAsync())}
                disabled={busy === 'portal'}
                className="text-xs font-medium text-[color:var(--bill-primary)] hover:underline disabled:opacity-60"
              >
                {paymentMethod ? t('payment.edit') : t('payment.add')}
              </button>
            </div>
            <button
              onClick={() => void redirectTo(() => portalMut.mutateAsync())}
              disabled={busy === 'portal'}
              className={`${CARD} group w-full p-4 flex items-center justify-between gap-3 text-left hover:themed-bg-tertiary transition-colors`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-8 rounded-md themed-bg-tertiary flex items-center justify-center shrink-0">
                  <CreditCard size={16} className="themed-text-muted" />
                </div>
                {paymentMethod ? (
                  <div className="min-w-0">
                    {/* Stripe brand strings are lowercase ('visa') — title-case it. */}
                    <p className="text-sm themed-text font-medium truncate">
                      {t('payment.endingIn', {
                        brand: paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1),
                        last4: paymentMethod.last4,
                      })}
                    </p>
                    <p className={`text-xs themed-text-muted ${NUMERIC}`}>
                      {t('payment.expires', { date: formatCardExp(paymentMethod.expMonth, paymentMethod.expYear) })}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm themed-text-muted">{t('payment.none')}</p>
                )}
              </div>
              {busy === 'portal' ? (
                <Loader2 size={16} className="animate-spin themed-text-muted shrink-0" />
              ) : (
                <Settings size={16} className="themed-text-muted group-hover:text-[color:var(--bill-primary)] transition-colors shrink-0" />
              )}
            </button>
            {/* Cancel — a quiet red text link (revised design). Hidden only when
                a cancellation is genuinely the pending outcome; a queued plan
                change leaves it available (same shared derivation). */}
            {!scheduledCancel && (
              <button
                onClick={() => void cancel()}
                disabled={busy === 'cancel'}
                className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1.5 transition-colors disabled:opacity-60"
              >
                {busy === 'cancel' ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                {t('cancel.action')}
              </button>
            )}
          </div>
        )}

        {/* Invoice history — the design's ledger table. Always shown for billing
            admins (empty state when there's nothing yet). Links go to Stripe's
            hosted document — that PDF, not our mirror, is the authoritative
            record the customer can present. */}
        {/* Rendered when there is history to show, NOT when a provider decides
            plans. The finding was an EMPTY table, and emptiness is the honest
            test: a deployment that moved from Stripe to manual invoicing still
            has real invoices behind it, and gating on authority would hide a
            customer's own billing history the day the switch was flipped. */}
        {canAct && invoices.length > 0 && (
          <div className="space-y-3">
            <h3 className={`${SECTION_LABEL} flex items-center gap-1.5`}>
              <Receipt size={12} />
              {t('invoices.heading')}
            </h3>
            <div className={`${CARD} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="text-[10px] themed-text-muted uppercase tracking-[0.08em] border-b themed-border">
                    <th className="font-semibold px-5 py-3">{t('invoices.col.date')}</th>
                    <th className="font-semibold px-5 py-3">{t('invoices.col.amount')}</th>
                    <th className="font-semibold px-5 py-3">{t('invoices.col.status')}</th>
                    <th className="font-semibold px-5 py-3 text-right">{t('invoices.col.action')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[color:var(--border-primary)]">
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-5 py-6 text-sm themed-text-muted text-center">
                        {t('invoices.empty')}
                      </td>
                    </tr>
                  ) : (
                    invoices.map((inv) => (
                      <tr key={inv.id} className="hover:themed-bg-tertiary transition-colors">
                        <td className={`px-5 py-3 text-sm themed-text-secondary ${NUMERIC}`}>{fmtDate(inv.periodStart)}</td>
                        {/* The INVOICE's own currency, mirrored from the
                            provider at settlement — not the plan's current one.
                            A historical invoice must keep reading in the
                            currency it was actually paid in even after the
                            catalog moves to another. */}
                        <td className={`px-5 py-3 text-sm themed-text font-medium ${NUMERIC}`}>{formatCurrencyMinor(inv.totalMinor, inv.currency ?? FALLBACK_CURRENCY)}</td>
                        <td className="px-5 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${invoiceStatusTone(inv.status)}`}>
                            {t(`invoices.status.${inv.status}`, inv.status)}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          {inv.hostedInvoiceUrl && (
                            <a
                              href={inv.hostedInvoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={t('invoices.view')}
                              aria-label={t('invoices.view')}
                              className="inline-flex text-[color:var(--bill-primary)] hover:opacity-80 transition-colors"
                            >
                              <ExternalLink size={15} />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}
      </section>

      {/* ── Subscription-term history (Task 19) ──────────────────────────
          Read-only mirror of the platform console's own timeline
          (`SubscriptionTermsPanel` in `CustomerManualBilling.tsx`) — this is
          the org looking at its OWN plan/cycle history, so there are no
          add/delete/correct controls: those are an OPERATOR's levers over
          ANOTHER tenant's contract, not something a tenant does to itself.
          `GET /subscription-terms` is self-scoped (no tenant param) and
          already gated `manage_billing:read` server-side; see
          `canViewSubscriptionTerms`'s doc on `Props` for why this renders
          ungated by default and how a host or test can gate it explicitly. */}
      {!subscriptionTermsLoading && showSubscriptionTerms && (
        <SubscriptionTermsSection terms={termsQuery.data?.terms ?? []} plans={plans} t={t} currency={currency} />
      )}
    </div>
  )
}

/**
 * The read-only timeline: plan, start date, cycle and a past/current/future
 * badge per term, newest-first — plus the current allowance reset day,
 * derived from the current term's `effectiveFrom` (Task 19 requirement 2).
 *
 * Sorts defensively even though the server already returns newest-first
 * (`listSubscriptionTerms`'s own doc comment) — cheap, and it means this
 * component's own contract doesn't depend on the wire's.
 */
function SubscriptionTermsSection({
  terms, plans, t, currency,
}: {
  terms: SubscriptionTermView[]
  plans: PlanView[]
  t: (k: string, o?: Record<string, unknown>) => string
  /** The page's billing currency — term dates render in its locale, matching
   *  the amounts shown elsewhere on the page. */
  currency: string
}) {
  const sorted = [...terms].sort(
    (a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime(),
  )
  const currentTerm = sorted.find((term) => term.status === 'current') ?? null
  // `effectiveFrom` is midnight UTC of the day the term takes effect (see the
  // wire type's own doc comment) — `getUTCDate()`, not `getDate()`, or an
  // operator west of Greenwich would see the reset day shift back by one.
  const resetDay = currentTerm ? new Date(currentTerm.effectiveFrom).getUTCDate() : null

  return (
    <section className={`${CARD} p-5 space-y-3`} data-billing-terms-section>
      <h3 className={`${SECTION_LABEL} flex items-center gap-1.5`}>
        <CalendarClock size={12} />
        {t('terms.heading')}
      </h3>
      {resetDay != null && (
        <p className="themed-text-secondary text-xs">{t('terms.resetDay', { day: resetDay })}</p>
      )}
      {sorted.length === 0 ? (
        <p className="themed-text-muted text-xs">{t('terms.empty')}</p>
      ) : (
        <ul className="divide-y themed-border">
          {sorted.map((term) => (
            <li key={term.id} className="flex items-center gap-2 py-2">
              <TermStatusBadge status={term.status} label={t(`terms.status.${term.status}`)} />
              <span className="themed-text text-sm flex-1 truncate">
                {term.planName ?? plans.find((p) => p.id === term.planId)?.name ?? term.planId}
              </span>
              <span className={`themed-text-muted text-xs ${NUMERIC}`}>{formatCurrencyDate(term.effectiveFrom, currency)}</span>
              <span className="themed-text-muted text-xs">{termCycleLabel(term, t)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** past/current/future — same tone scheme as the platform console's own
 *  `StatusBadge` (`CustomerManualBilling.tsx`), so the two timelines read as
 *  one visual language even though they render different data. */
function TermStatusBadge({ status, label }: { status: SubscriptionTermView['status']; label: string }) {
  const tone =
    status === 'current'
      ? 'bg-emerald-500/12 text-emerald-400 ring-emerald-500/25'
      : status === 'future'
        ? 'bg-blue-500/12 text-blue-400 ring-blue-500/25'
        : 'themed-bg-tertiary themed-text-secondary ring-transparent'
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ring-1 ${tone}`}>
      {label}
    </span>
  )
}

/** "/ month" / "every 2 weeks" — a term's billing rhythm, compact (sibling of
 *  the platform console's `termCycleLabel`, same shape, different namespace). */
function termCycleLabel(
  term: { cycleCount: number; cycleUnit: string },
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  if (term.cycleCount === 1) {
    return t(`terms.cycle.${term.cycleUnit}`, {
      defaultValue:
        ({ day: '/ day', week: '/ week', month: '/ month', year: '/ year' } as Record<string, string>)[
          term.cycleUnit
        ] ?? `/ ${term.cycleUnit}`,
    })
  }
  return t('terms.cycle.everyN', {
    defaultValue: 'every {{count}} {{unit}}s', count: term.cycleCount, unit: term.cycleUnit,
  })
}

/** Card expiry as MM/YY (Stripe gives numeric month + 4-digit year). */
function formatCardExp(month: number, year: number): string {
  return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`
}

/** Invoice status → pill tone. Colour is never the only signal — the label
 *  text states the same status beside it. */
function invoiceStatusTone(status: string): string {
  switch (status) {
    case 'paid':
      return 'bg-emerald-500/12 text-emerald-400'
    case 'open':
      return 'bg-amber-500/12 text-amber-400'
    case 'void':
    case 'uncollectible':
      return 'bg-red-500/12 text-red-400'
    default:
      return 'themed-bg-tertiary themed-text-muted'
  }
}

// Dates on this customer money screen render in the DISPLAY currency's locale
// (`fmtDate` closure in the component, and `formatCurrencyDate` in the term
// section) so their order matches the amounts beside them. `formatCurrencyDate`
// is UTC-pinned, which also fixes the west-of-Greenwich day-shift the old
// `formatDate`/`formatDateUTC` split existed to manage.
